import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useSpring } from 'framer-motion';

const Header = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('slideshow');
  const loginBtnRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  // ===== SCROLL PROGRESS BAR =====
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  // ===== ACTIVE SECTION DETECTION =====
  const sectionIds = ['slideshow', 'housing-services', 'career', 'director-details', 'testimonials', 'contact'];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-40% 0px -55% 0px', threshold: 0 }
    );

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  // ===== SCROLL HANDLER =====
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => setIsOpen(!isOpen);
  const closeMenu = () => setIsOpen(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleNavClick = (link) => {
    if (link.sectionId) {
      scrollToSection(link.sectionId);
      setActiveSection(link.sectionId);
    } else if (link.route) {
      navigate(link.route);
    }
    closeMenu();
  };

  const navLinks = [
    { label: 'Home', sectionId: 'slideshow' },
    { label: 'Services', sectionId: 'housing-services' },
    { label: 'Careers', sectionId: 'career' },
    { label: 'About', sectionId: 'director-details' },
    { label: 'Contact', sectionId: 'contact' },
  ];

  // ===== MAGNETIC BUTTON HANDLER =====
  const handleMouseMove = (e) => {
    if (!loginBtnRef.current) return;
    const rect = loginBtnRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    setMousePos({ x: x * 0.15, y: y * 0.1 });
  };

  const handleMouseLeave = () => {
    setMousePos({ x: 0, y: 0 });
  };

  return (
    <>
      {/* ===== SCROLL PROGRESS BAR ===== */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-[3px] z-[9999] origin-left"
        style={{
          scaleX,
          background: 'linear-gradient(90deg, #6FCEEC, #1976D2, #00B8D9)',
          boxShadow: '0 0 10px rgba(111, 206, 236, 0.5)',
        }}
      />

      {/* ===== DIRECT NAVBAR (No top strip as requested) ===== */}
      <header className="fixed top-0 left-0 w-full z-[2000]">
        <motion.div
          animate={{
            height: isScrolled ? 70 : 76,
          }}
          transition={{ duration: 0.25, ease: 'easeInOut' }}
          style={{
            backgroundColor: 'transparent',
            boxShadow: 'none',
            borderBottom: 'none',
          }}
          className="w-full"
        >
          <div className="max-w-7xl mx-auto px-6 h-full flex items-center justify-between">
            {/* Logo (AVA India style: clean brand mark + slogan) */}
            <button
              onClick={() => scrollToSection('slideshow')}
              className="flex items-center gap-3 group cursor-pointer border-0 bg-transparent p-0 text-left"
            >
              <img
                src={process.env.PUBLIC_URL + '/images/logo_1.png'}
                alt="Aryavihhar Logo"
                className="h-11 w-auto drop-shadow-sm transition-transform duration-200 group-hover:scale-105"
              />
              <div className="hidden sm:block">
                <span className="block text-[#1976D2] font-bold text-[1.12rem] tracking-tight leading-none">
                  Aryavihhar
                </span>
                <span className="block text-[#001726]/65 font-medium text-[0.78rem] tracking-tight mt-1">
                  Growing Together & Protecting You...
                </span>
              </div>
            </button>

            {/* Nav Links (Clean AVA India typography) */}
            <nav className="hidden lg:flex items-center gap-1 xl:gap-2">
              {navLinks.map((link) => {
                const isActive = activeSection === link.sectionId;
                return (
                  <button
                    key={link.label}
                    onClick={() => handleNavClick(link)}
                    className={`relative px-3.5 py-2 text-[0.95rem] font-semibold transition-all duration-200 border-0 bg-transparent cursor-pointer ${isActive
                      ? 'text-[#00B8D9]'
                      : 'text-[#001726] hover:text-[#00B8D9]'
                      }`}
                  >
                    {link.label}
                    {isActive && (
                      <motion.div
                        layoutId="navIndicator"
                        className="absolute bottom-0 left-3.5 right-3.5 h-[2px] bg-[#00B8D9] rounded-full"
                        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                      />
                    )}
                  </button>
                );
              })}

              {/* Quick Action Icons & Button (Matching AVA India header screenshot) */}
              <div className="flex items-center gap-2.5 ml-4 pl-4 border-l border-[#2C61B924]">
                {/* Email Icon Button */}
                <a
                  href="mailto:arvihharinfo@gmail.com"
                  title="Email Us"
                  className="w-9 h-9 rounded-xl bg-[#FEE2E2] hover:bg-[#EF4444] text-[#EF4444] hover:text-white flex items-center justify-center transition-all duration-200 shadow-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </a>

                {/* Phone Icon Button */}
                <a
                  href="tel:9234804849"
                  title="Call Us"
                  className="w-9 h-9 rounded-xl bg-[#DCFCE7] hover:bg-[#22C55E] text-[#16A34A] hover:text-white flex items-center justify-center transition-all duration-200 shadow-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </a>

                {/* Contact Us Pill CTA Button (AVA India screenshot style) */}
                <button
                  type="button"
                  onClick={() => scrollToSection('contact')}
                  className="px-5 py-2.5 rounded-xl text-white font-semibold text-sm tracking-wide flex items-center gap-2 border-0 cursor-pointer shadow-sm transition-all duration-200 hover:brightness-105"
                  style={{
                    background: '#6FCEEC',
                    color: '#001726',
                  }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span>Contact Us</span>
                </button>

                {/* Login Button */}
                <motion.button
                  ref={loginBtnRef}
                  type="button"
                  // onClick={() => navigate('/login')}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                  className="px-5 py-2.5 rounded-xl text-white font-semibold text-sm tracking-wide flex items-center gap-1.5 border-0 cursor-pointer shadow-md"
                  style={{
                    background: 'linear-gradient(135deg, #1976D2 0%, #00B8D9 100%)',
                    boxShadow: '0 4px 14px rgba(25, 118, 210, 0.25)',
                  }}
                  animate={{
                    x: mousePos.x,
                    y: mousePos.y,
                  }}
                  transition={{ type: 'spring', stiffness: 150, damping: 15 }}
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span>Login</span>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                  </svg>
                </motion.button>
              </div>
            </nav>

            {/* Mobile Hamburger */}
            <button
              type="button"
              onClick={toggleMenu}
              aria-label="Toggle navigation"
              aria-expanded={isOpen}
              className="lg:hidden flex flex-col justify-center items-center w-11 h-11 gap-1.5 rounded-xl border border-[#2C61B924] bg-[#EAF7FB] hover:bg-[#d9eff7] transition-all duration-200 cursor-pointer"
            >
              <span className={`block w-5 h-0.5 bg-[#1976D2] rounded-full transition-transform duration-200 ${isOpen ? 'rotate-45 translate-y-2' : ''}`} />
              <span className={`block w-5 h-0.5 bg-[#1976D2] rounded-full transition-opacity duration-200 ${isOpen ? 'opacity-0' : ''}`} />
              <span className={`block w-5 h-0.5 bg-[#1976D2] rounded-full transition-transform duration-200 ${isOpen ? '-rotate-45 -translate-y-2' : ''}`} />
            </button>
          </div>
        </motion.div>

        {/* Mobile Drawer */}
        <AnimatePresence>
          {isOpen && (
            <>
              <motion.button
                type="button"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={closeMenu}
                className="fixed inset-0 z-[1998] bg-[#001726]/30 backdrop-blur-sm border-0 cursor-default w-full"
              />
              <motion.nav
                initial={{ x: '100%', opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: '100%', opacity: 0 }}
                transition={{ type: 'tween', duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="fixed top-0 right-0 z-[1999] h-full w-[min(340px,85vw)] bg-white shadow-2xl flex flex-col border-l border-[#2C61B924]"
              >
                {/* Drawer Header */}
                <div className="p-6 pt-7 pb-5 border-b border-[#2C61B924] flex items-center justify-between bg-[#F4FBFE]">
                  <div className="flex items-center gap-3">
                    <img
                      src={process.env.PUBLIC_URL + '/images/log.png'}
                      alt="Aryavihhar"
                      className="h-10 w-auto"
                    />
                    <div className="text-left">
                      <p className="text-[#1976D2] font-bold text-sm leading-tight m-0">Aryavihhar</p>
                      <p className="text-[#001726]/60 text-[10px] font-semibold m-0">Security Services</p>
                    </div>
                  </div>
                  <button
                    onClick={closeMenu}
                    className="w-9 h-9 flex items-center justify-center rounded-xl bg-[#EAF7FB] hover:bg-[#d9eff7] text-[#1976D2] transition-all duration-200 text-lg border-0 cursor-pointer"
                  >
                    ✕
                  </button>
                </div>

                <ul className="flex flex-col gap-1.5 p-5 pt-6 flex-1 list-none m-0">
                  {navLinks.map((link) => {
                    const isActive = activeSection === link.sectionId;
                    return (
                      <li key={link.label}>
                        <button
                          onClick={() => handleNavClick(link)}
                          className={`w-full flex items-center px-4 py-3 text-[0.95rem] font-semibold rounded-xl transition-all duration-200 border-0 cursor-pointer ${isActive
                            ? 'text-[#00B8D9] bg-[#EAF7FB] border border-[#2C61B924]'
                            : 'text-[#001726] hover:text-[#00B8D9] hover:bg-[#F4FBFE] bg-transparent'
                            }`}
                        >
                          <span className={`w-2 h-2 rounded-full mr-3 transition-all duration-300 ${isActive ? 'bg-[#00B8D9]' : 'bg-[#D9E6EE]'
                            }`} />
                          {link.label}
                        </button>
                      </li>
                    );
                  })}
                  <li className="mt-4 flex flex-col gap-2.5">
                    <button
                      type="button"
                      onClick={() => { closeMenu(); scrollToSection('contact'); }}
                      className="w-full flex items-center justify-center px-4 py-3 text-[#001726] font-semibold text-[0.95rem] rounded-xl bg-[#6FCEEC] border-0 cursor-pointer"
                    >
                      Contact Us
                    </button>
                    <button
                      type="button"
                      onClick={() => { closeMenu(); navigate('/login'); }}
                      className="w-full flex items-center justify-center px-4 py-3 text-white font-semibold text-[0.95rem] rounded-xl bg-gradient-to-r from-[#1976D2] to-[#00B8D9] shadow-md border-0 cursor-pointer"
                    >
                      Login
                    </button>
                  </li>
                </ul>

                <div className="p-5 border-t border-[#2C61B924] bg-[#F4FBFE]">
                  <p className="text-[#001726]/40 text-[10px] tracking-[0.15em] uppercase text-center font-semibold m-0">
                    Aryavihhar Security Services
                  </p>
                </div>
              </motion.nav>
            </>
          )}
        </AnimatePresence>
      </header>
    </>
  );
};

export default Header;